import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../lib/apiClient';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@pagespeedify/ui';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth'; // <-- Make sure this is imported

const steps = [
    "🚀 Queueing your analysis in our performance lab...",
    "🔥 Warming up the Lighthouse engine...",
    "📊 Running comprehensive performance audit...",
    "⚡ Analyzing Core Web Vitals (LCP, INP, CLS)...", 
    "🔍 Deep-diving into resource loading patterns...",
    "🎯 Identifying high-impact optimization opportunities...",
    "📈 Calculating potential performance improvements...",
    "🛠️ Compiling actionable recommendations...",
    "✨ Finalizing your personalized report...",
];

interface ReportGenerationStatusProps {
    reportId?: string; // Changed to optional
    onBack: () => void;
}

interface ReportStatusError {
    name: string;
    message: string;
    details?: any;
}

interface ReportStatus {
    status: 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILED';
    reportId?: string;
    error?: ReportStatusError | string; // Allow string for backward compatibility or simple errors
    message?: string;
    // Auto-creation flags (to avoid extra R2 calls)
    createTasksRequested?: boolean;
    aiSolutionsRequested?: boolean;
    // Task creation status
    tasksCreated?: boolean;
    taskCreationStatus?: 'PENDING' | 'IN_PROGRESS' | 'SUCCESS' | 'FAILED';
    taskCount?: number;
    taskCreationError?: string;
    // AI solutions status (for future)
    aiSolutionsCreated?: boolean;
    aiSolutionsStatus?: 'PENDING' | 'IN_PROGRESS' | 'SUCCESS' | 'FAILED';
}

export const ReportGenerationStatus: React.FC<ReportGenerationStatusProps> = ({ reportId, onBack }) => {
    const apiClient = useApi();
    const queryClient = useQueryClient();
    const { user, refreshUser } = useAuth(); // get refreshUser if available
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const [isPollingEnabled, setIsPollingEnabled] = useState(false);
    const [displayError, setDisplayError] = useState<ReportStatusError | null>(null);
    const [startTime] = useState(Date.now());
    const [reportSuccessTime, setReportSuccessTime] = useState<number | null>(null);
    const [hasTimedOut, setHasTimedOut] = useState(false);

    // Timeout constants - different timeouts for different phases
    const MAX_TOTAL_TIMEOUT = 15 * 60 * 1000; // 15 minutes absolute maximum (fallback)
    const REPORT_TIMEOUT = 10 * 60 * 1000; // 10 minutes for report generation
    const POST_REPORT_TIMEOUT = 5 * 60 * 1000; // 5 minutes after report success
    const TASKS_ONLY_TIMEOUT = 2 * 60 * 1000; // 2 minutes if only tasks are running (no AI)

    // Calculate dynamic timeout based on current phase
    const getCurrentTimeout = (currentData: ReportStatus | null): number => {
        const currentTime = Date.now();
        
        // If report hasn't succeeded yet, use report timeout
        if (!currentData || currentData.status !== 'SUCCESS') {
            return REPORT_TIMEOUT;
        }
        
        // Report succeeded - track when this happened
        if (!reportSuccessTime) {
            setReportSuccessTime(currentTime);
        }
        
        // If only tasks are being created (no AI), use shorter timeout
        if (currentData.createTasksRequested && !currentData.aiSolutionsRequested) {
            return TASKS_ONLY_TIMEOUT;
        }
        
        // Otherwise use the general post-report timeout
        return POST_REPORT_TIMEOUT;
    };

    // --- Add this logic to determine the correct userId for status lookup ---
    const internalUserId = user?.internalUserId;
    const isInTeam = !!user?.team_id;
    const isTeamOwner = user?.team_id && user?.team_owner_id === user?.internalUserId;
    // For status polling: use team owner's internalUserId for team members, otherwise self
    const statusPollingUserId = isInTeam && !isTeamOwner ? user?.team_owner_id : internalUserId;

    // Handle report ready logic - check auto-creation status and wait if needed
    const handleReportReady = async (reportData: ReportStatus) => {
        try {
            console.log('[handleReportReady] Checking auto-creation status', { 
                reportId: reportData.reportId,
                createTasksRequested: reportData.createTasksRequested,
                aiSolutionsRequested: reportData.aiSolutionsRequested,
                taskCreationStatus: reportData.taskCreationStatus,
                aiSolutionsStatus: reportData.aiSolutionsStatus
            });
            
            // Use status file data directly instead of making extra R2 API call
            const createTasksRequested = reportData.createTasksRequested || false;
            const aiSolutionsRequested = reportData.aiSolutionsRequested || false;

            console.log('[handleReportReady] Auto-creation flags from status file:', { 
                createTasksRequested, 
                aiSolutionsRequested,
                reportId: reportData.reportId,
                taskCreationStatus: reportData.taskCreationStatus,
                aiSolutionsStatus: reportData.aiSolutionsStatus
            });

            // Check if any requested features are still in progress
            let stillWaiting = false;
            let hasFailures = false;
            let successMessages: string[] = [];
            let errorMessages: string[] = [];

            // Check task creation status
            if (createTasksRequested) {
                const taskStatus = reportData.taskCreationStatus;
                
                if (taskStatus === 'IN_PROGRESS' || taskStatus === 'PENDING') {
                    console.log('[handleReportReady] Task creation in progress, continuing to poll');
                    stillWaiting = true;
                } else if (taskStatus === 'SUCCESS') {
                    console.log('[handleReportReady] Tasks created successfully', { 
                        taskCount: reportData.taskCount 
                    });
                    successMessages.push(`${reportData.taskCount || 0} optimization tasks created`);
                } else if (taskStatus === 'FAILED') {
                    console.warn('[handleReportReady] Task creation failed', { 
                        error: reportData.taskCreationError 
                    });
                    hasFailures = true;
                    errorMessages.push(`Task creation failed: ${reportData.taskCreationError}`);
                }
            }

            // Check AI solutions status
            if (aiSolutionsRequested) {
                const aiStatus = reportData.aiSolutionsStatus;
                
                console.log('[handleReportReady] Checking AI solutions status:', {
                    aiStatus,
                    aiSolutionsCreated: reportData.aiSolutionsCreated
                });
                
                if (aiStatus === 'IN_PROGRESS' || aiStatus === 'PENDING') {
                    console.log('[handleReportReady] AI solutions in progress, continuing to poll');
                    stillWaiting = true;
                } else if (!aiStatus && !reportData.aiSolutionsCreated) {
                    console.log('[handleReportReady] AI status undefined and not created, assuming still waiting');
                    stillWaiting = true;
                } else if (aiStatus === 'SUCCESS') {
                    console.log('[handleReportReady] AI solutions created successfully');
                    successMessages.push('AI-powered optimization suggestions generated');
                } else if (aiStatus === 'FAILED') {
                    console.warn('[handleReportReady] AI solutions creation failed');
                    hasFailures = true;
                    errorMessages.push('AI suggestions creation failed');
                }
            }

            // If still waiting for any feature, continue polling
            if (stillWaiting) {
                return;
            }

            // All requested features are complete - show final toast and redirect
            if (successMessages.length > 0 && !hasFailures) {
                // All successes
                const message = successMessages.length === 1 
                    ? `Report generated with ${successMessages[0]}!`
                    : `Report generated with ${successMessages.join(' and ')}!`;
                toast.success(message);
            } else if (successMessages.length > 0 && hasFailures) {
                // Mixed results
                toast.success(`Report generated with ${successMessages.join(' and ')}!`);
                errorMessages.forEach(error => toast.error(error));
            } else if (hasFailures && successMessages.length === 0) {
                // All failures
                toast.error(`Report generated but ${errorMessages.join(' and ')}`);
            } else {
                // No auto-creation requested or unknown state
                toast.success('Report generated successfully!');
            }

            // If we reach here, all requested auto-creation features are complete
            console.log('[handleReportReady] All auto-creation features completed, redirecting to report');
            redirectToReport(reportData.reportId || '');
            
        } catch (error) {
            console.error('[handleReportReady] Error checking auto-creation status:', error);
            // If there's an error, just redirect to the report page
            redirectToReport(reportData.reportId || '');
        }
    };

    // Helper function to handle the actual redirection and cleanup
    const redirectToReport = (reportId: string) => {
        // Invalidate all relevant queries and refresh user context before navigating
        queryClient.invalidateQueries({ queryKey: ['reportUsage', user?.uid] });
        queryClient.invalidateQueries({ queryKey: ['ReportsHistory', user?.internalUserId] });
        queryClient.invalidateQueries({ queryKey: ['creditHistory', user?.uid] });
        queryClient.invalidateQueries({ queryKey: ['creditBalance', user?.uid] });
        
        // Invalidate task list queries so sidebar updates with new tasks
        queryClient.invalidateQueries({ queryKey: ['ai-tasks-list'] });
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
        
        if (typeof refreshUser === 'function') refreshUser();
        
        console.log('[redirectToReport] Navigating to report:', reportId);
        navigate(`/reports/${reportId}`);
    };

    useEffect(() => {
        // Start polling after a 10-second delay to give the backend time to initialize.
        const timer = setTimeout(() => {
            setIsPollingEnabled(true);
        }, 10000); 

        // Set up timeout monitoring - use absolute maximum timeout
        const timeoutTimer = setTimeout(() => {
            setHasTimedOut(true);
            setDisplayError({
                name: 'TimeoutError',
                message: 'Report generation is taking longer than expected. This may indicate a service issue. Please try again or contact support if the problem persists.'
            });
        }, MAX_TOTAL_TIMEOUT);

        return () => {
            clearTimeout(timer);
            clearTimeout(timeoutTimer);
        };
    }, []); // No dependencies needed - absolute timeout set once

    const { data, error, isLoading } = useQuery<ReportStatus | null, Error>({
        queryKey: ['reportGenerationStatus', reportId, statusPollingUserId],
        queryFn: async () => {
            if (!apiClient) throw new Error('API client not available');
            if (!statusPollingUserId) throw new Error('User ID not available');
            
            // Check for timeout
            if (hasTimedOut) {
                throw new Error('Polling timeout reached');
            }
            
            console.log('[queryFn] Fetching report status', { 
                reportId, 
                statusPollingUserId,
                timestamp: new Date().toISOString()
            });
            
            try {
                // Always use the correct internalUserId for status polling (team owner for team members)
                const result = await apiClient.get<ReportStatus>(`/reports/status?internalUserId=${statusPollingUserId}&reportId=${reportId}`);
                
                console.log('[queryFn] Status response received', {
                    status: result.status,
                    taskCreationStatus: result.taskCreationStatus,
                    aiSolutionsStatus: result.aiSolutionsStatus,
                    createTasksRequested: result.createTasksRequested,
                    aiSolutionsRequested: result.aiSolutionsRequested
                });
                
                return result;
            } catch (error: any) {
                if (error.response?.status === 404) {
                    console.log('[queryFn] 404 - Status file not found yet');
                    throw error;
                }
                console.error('[queryFn] Error fetching status:', error.message);
                throw error; // Rethrow other errors to be handled by react-query
            }
        },
        refetchInterval: (query) => {
            // Stop polling if timed out
            if (hasTimedOut) {
                return false;
            }
            
            const statusData = query.state.data;
            
            // If report failed, stop polling
            if (statusData && statusData.reportId === reportId && statusData.status === 'FAILED') {
                return false;
            }
            
            // If report succeeded, check if auto-creation features are complete
            if (statusData && statusData.reportId === reportId && statusData.status === 'SUCCESS') {
                const createTasksRequested = statusData.createTasksRequested || false;
                const aiSolutionsRequested = statusData.aiSolutionsRequested || false;
                
                console.log('[refetchInterval] Report SUCCESS - checking auto-creation status', {
                    createTasksRequested,
                    aiSolutionsRequested,
                    taskCreationStatus: statusData.taskCreationStatus,
                    aiSolutionsStatus: statusData.aiSolutionsStatus,
                    tasksCreated: statusData.tasksCreated,
                    aiSolutionsCreated: statusData.aiSolutionsCreated
                });
                
                // Check if any auto-creation features are still in progress
                let stillWaiting = false;
                
                if (createTasksRequested) {
                    const taskStatus = statusData.taskCreationStatus;
                    console.log('[refetchInterval] Checking task status:', { taskStatus, expected: ['IN_PROGRESS', 'PENDING'] });
                    if (taskStatus === 'IN_PROGRESS' || taskStatus === 'PENDING') {
                        console.log('[refetchInterval] Tasks still in progress, waiting...');
                        stillWaiting = true;
                    }
                }
                
                if (aiSolutionsRequested) {
                    const aiStatus = statusData.aiSolutionsStatus;
                    console.log('[refetchInterval] Checking AI status:', { 
                        aiStatus, 
                        expected: ['IN_PROGRESS', 'PENDING'],
                        aiSolutionsCreated: statusData.aiSolutionsCreated 
                    });
                    if (aiStatus === 'IN_PROGRESS' || aiStatus === 'PENDING') {
                        console.log('[refetchInterval] AI solutions still in progress, waiting...');
                        stillWaiting = true;
                    } else if (!aiStatus && !statusData.aiSolutionsCreated) {
                        // If aiStatus is undefined/null and aiSolutionsCreated is false, AI might not have started yet
                        console.log('[refetchInterval] AI status undefined and not created, assuming still waiting...');
                        stillWaiting = true;
                    }
                }
                
                // Only stop polling if no auto-creation features are still in progress
                if (!stillWaiting) {
                    console.log('[refetchInterval] All auto-creation features complete, stopping polling', {
                        taskCreationStatus: statusData.taskCreationStatus,
                        aiSolutionsStatus: statusData.aiSolutionsStatus
                    });
                    return false;
                }
                
                console.log('[refetchInterval] Auto-creation features still in progress, continuing polling', {
                    taskCreationStatus: statusData.taskCreationStatus,
                    aiSolutionsStatus: statusData.aiSolutionsStatus,
                    createTasksRequested,
                    aiSolutionsRequested
                });
            }

            // Stop polling if we've been trying for too long or too many failures
            const elapsedTime = Date.now() - startTime;
            const currentData = query.state.data;
            const dynamicTimeout = getCurrentTimeout(currentData || null);
            
            // Use dynamic timeout based on current phase
            if (elapsedTime > dynamicTimeout || query.state.errorUpdateCount > 15) {
                console.log(`Dynamic timeout reached: ${elapsedTime}ms > ${dynamicTimeout}ms`, {
                    reportStatus: currentData?.status,
                    hasReportSuccessTime: !!reportSuccessTime,
                    createTasksRequested: currentData?.createTasksRequested,
                    aiSolutionsRequested: currentData?.aiSolutionsRequested
                });
                return false;
            }

            // Programmatic incremental backoff: 10s, 15s, 20s, 25s, ..., 60s, 65s, 70s, ...
            // Each interval is previous + 5s, starting from 10s, capped at 60s minimum after 4th poll
            const fetchCount = query.state.dataUpdateCount;
            let interval = 10000; // 10s
            for (let i = 2; i <= fetchCount; i++) {
                interval = interval + 5000;
            }
            return Math.min(interval, 30000); // Cap at 30 seconds max interval
        },
        enabled: !!apiClient && !!reportId && !!statusPollingUserId && isPollingEnabled && !hasTimedOut,
        // More restrictive retry logic
        retry: (failureCount, error: any) => {
            // Don't retry if timed out
            if (hasTimedOut) return false;
            
            if (error?.response?.status === 404) {
                return failureCount < 5; // Retry up to 5 times for 404s (total ~15s)
            }
            return failureCount < 8; // Limit retries for other errors
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff capped at 10s
    });

    useEffect(() => {
        if (data && data.reportId === reportId) {
            if (data.status === 'SUCCESS') {
                // Report is ready - now check if we need to wait for auto-creation features
                console.log('[useEffect] Report ready, checking auto-creation status', { 
                    reportId: data.reportId,
                    taskCreationStatus: data.taskCreationStatus,
                    aiSolutionsStatus: data.aiSolutionsStatus
                });
                
                handleReportReady(data);
            } else if (data.status === 'FAILED') {
                let errorToDisplay: ReportStatusError = { name: 'UnknownError', message: 'Report generation failed. Please try again.' };
                let finalMessage = '';

                if (typeof data.error === 'string') {
                    finalMessage = data.error;
                } else if (data.error && typeof data.error === 'object') {
                    errorToDisplay.name = data.error.name || 'Error';
                    let messageSource = data.error.message || 'An unspecified error occurred during report generation.';

                    // Prioritize specific message from PageSpeedApiError details
                    if (data.error.name === 'PageSpeedApiError' &&
                        data.error.details &&
                        typeof data.error.details === 'object' &&
                        (data.error.details as any).error &&
                        typeof (data.error.details as any).error.message === 'string') {
                        messageSource = (data.error.details as any).error.message;
                    }

                    // Clean up prefixes
                    if (messageSource.startsWith("Lighthouse returned error: ")) {
                        finalMessage = messageSource.substring("Lighthouse returned error: ".length);
                    } else if (messageSource.startsWith("PageSpeed API error: ")) {
                        finalMessage = messageSource.substring("PageSpeed API error: ".length);
                    } else {
                        finalMessage = messageSource;
                    }

                    errorToDisplay.message = `API Error: ${finalMessage}`;

                    if (data.error.details) {
                        console.error("Report Generation Failed - Full Error Object:", data.error);
                    }
                } else {
                     errorToDisplay.message = 'Report generation failed. Please try again.';
                }
                // toast.error(errorToDisplay.message); // Removed toast
                setDisplayError(errorToDisplay);
            }
        } else if (error) { // This handles query errors (e.g., network issues, 500s from status endpoint)
            const anyError = error as any;
            if (anyError.response?.status !== 404) {
                // const queryErrorMessage = anyError.message || 'Could not retrieve report status due to a server or network error.';
                // toast.error(queryErrorMessage); // Removed toast

                const errorNameToDisplay = anyError.response?.data?.error?.name ||
                                           anyError.name ||
                                           'Network/Server Error';
                let errorMessageToDisplay = anyError.response?.data?.error?.message || anyError.message || 'Could not retrieve report status due to a server or network error.';
                 // Clean up prefixes for query errors too, if applicable from our backend
                if (errorMessageToDisplay.startsWith("PageSpeed API error: ")) {
                    errorMessageToDisplay = `API Error: ${errorMessageToDisplay.substring("PageSpeed API error: ".length)}`;
                } else if (errorMessageToDisplay.startsWith("Lighthouse returned error: ")) {
                     errorMessageToDisplay = `API Error: ${errorMessageToDisplay.substring("Lighthouse returned error: ".length)}`;
                }

                setDisplayError({ name: errorNameToDisplay, message: errorMessageToDisplay });
                const errorDetails = anyError.response?.data?.error?.details || anyError.stack;
                if (errorDetails) {
                    console.error("Status Query Error - Full Details:", errorDetails);
                }
            }
            // For 404s, we don't toast or set displayError from here, as polling might still be retrying.
            // The component will show "Checking for active report..." or similar.
        }
    }, [data, error, navigate, reportId, queryClient, user, refreshUser]);

    useEffect(() => {
        const interval = setInterval(() => {
            // Only animate steps if report is not ready yet
            if (!data || data.status !== 'SUCCESS') {
                setCurrentStep(prev => (prev + 1) % steps.length);
            }
        }, 4000);
        return () => clearInterval(interval);
    }, [data]);

    if (isLoading && !data) {
        return (
            <div className="flex flex-col items-center justify-center h-[70vh] text-center p-6">
                <Loader2 className="w-16 h-16 animate-spin text-primary mb-6" />
                <h2 className="text-2xl font-semibold text-foreground mb-2">Checking for active report...</h2>
                <p className="text-muted-foreground">This shouldn't take long.</p>
            </div>
        );
    }

    // If there's a displayError, show the error UI
    if (displayError) {
        return (
            <div className="flex flex-col items-center justify-center h-[70vh] text-center p-6 bg-card rounded-lg shadow-md">
                {/* Apply new classes for dark mode compatibility using CSS variables defined in globals.css */}
                <div
                    className="bg-report-fail-light text-report-fail border border-[hsl(var(--report-fail))] px-4 py-3 rounded relative mb-6 w-full max-w-lg text-left"
                    role="alert"
                >
                    {/* Display only the formatted message. The 'name' can be logged or used internally if needed. */}
                    <p className="font-medium">{displayError.message}</p>
                </div>
                <Button variant="outline" onClick={() => { setDisplayError(null); onBack(); }} className="mt-4">
                    Go Back
                </Button>
            </div>
        );
    }

    // Helper function to get current status message
    const getCurrentStatusMessage = () => {
        // Check for timeout or excessive failures
        const elapsedTime = Date.now() - startTime;
        const currentData = data || null;
        const dynamicTimeout = getCurrentTimeout(currentData);
        
        // Show warning when approaching timeout for current phase
        const warningThreshold = dynamicTimeout * 0.75; // 75% of current timeout
        if (elapsedTime > warningThreshold && elapsedTime < dynamicTimeout) {
            if (currentData?.status === 'SUCCESS') {
                // Report done, waiting for tasks/AI
                return "⏱️ Post-processing is taking longer than usual...";
            } else {
                // Still generating report
                return "⏱️ This is taking longer than usual. Our systems may be experiencing high load...";
            }
        }
        
        if (data?.status === 'SUCCESS') {
            // Report is ready, show auto-creation status
            const taskStatus = data.taskCreationStatus;
            const aiStatus = data.aiSolutionsStatus;
            
            if (taskStatus === 'IN_PROGRESS') {
                return `🔧 Creating optimization tasks... (${data.taskCount || 0} tasks found)`;
            } else if (taskStatus === 'SUCCESS') {
                return `✅ optimization tasks created successfully!`;
            } else if (taskStatus === 'FAILED') {
                return `⚠️ Task creation failed: ${data.taskCreationError || 'Unknown error'}`;
            } else if (aiStatus === 'IN_PROGRESS') {
                return `🤖 Generating AI-powered optimization suggestions...`;
            } else if (aiStatus === 'SUCCESS') {
                return `✅ AI optimization suggestions generated successfully!`;
            } else if (aiStatus === 'FAILED') {
                return `⚠️ AI suggestions failed to generate`;
            }
            
            return "🎉 Report is ready! Preparing to show you the results...";
        }
        
        // Show custom message or default step
        return data?.message || steps[currentStep];
    };

    // Helper function to calculate progress percentage
    const getProgressPercentage = () => {
        if (data?.status === 'SUCCESS') {
            // Report is ready, show auto-creation progress
            const taskStatus = data.taskCreationStatus;
            const aiStatus = data.aiSolutionsStatus;
            
            if (taskStatus === 'IN_PROGRESS') {
                return 85; // 85% when tasks are being created
            } else if (taskStatus === 'SUCCESS' || taskStatus === 'FAILED') {
                if (aiStatus === 'IN_PROGRESS') {
                    return 95; // 95% when AI suggestions are being created
                } else {
                    return 100; // 100% when everything is complete
                }
            }
            
            return 80; // 80% when report is ready but checking auto-creation
        }
        
        // Default progress based on current step
        return ((currentStep + 1) / steps.length) * 75; // Max 75% until report is ready
    };

    // Default loading/polling UI
    return (
        <div className="flex flex-col items-center justify-center h-[70vh] text-center p-6 bg-card rounded-lg shadow-md">
            <Loader2 className="w-20 h-20 animate-spin text-primary mb-8" />
            <h1 className="text-3xl font-bold text-foreground mb-4">Your Report is Brewing!</h1>
            <p className="text-lg text-muted-foreground mb-6 max-w-md">
                {getCurrentStatusMessage()}
            </p>
            
            {/* Show timeout warning based on current phase */}
            {(() => {
                const elapsedTime = Date.now() - startTime;
                const currentData = data || null;
                const dynamicTimeout = getCurrentTimeout(currentData);
                const warningThreshold = dynamicTimeout * 0.8; // 80% of current timeout
                
                return elapsedTime > warningThreshold && !hasTimedOut && (
                    <div className="mb-6 p-4 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg max-w-md">
                        <p className="text-sm text-orange-800 dark:text-orange-200 mb-2">
                            ⚠️ This is taking longer than usual
                        </p>
                        <p className="text-xs text-orange-600 dark:text-orange-300">
                            {currentData?.status === 'SUCCESS' 
                                ? `Task creation and AI processing may take up to ${Math.ceil(dynamicTimeout / 60000)} minutes total.`
                                : `Report generation may take up to ${Math.ceil(dynamicTimeout / 60000)} minutes.`
                            }
                        </p>
                    </div>
                );
            })()}
            
            {/* Show additional task creation details if available */}
            {data?.status === 'SUCCESS' && data.taskCreationStatus === 'IN_PROGRESS' && (
                <div className="mb-6 p-4 bg-muted/50 rounded-lg max-w-md">
                    <p className="text-sm text-muted-foreground mb-2">
                        Analyzing your page performance data to create actionable optimization tasks...
                    </p>
                    {data.taskCount && data.taskCount > 0 && (
                        <p className="text-sm font-medium text-foreground">
                            Found {data.taskCount} optimization opportunities
                        </p>
                    )}
                </div>
            )}
            
            {/* Show AI status if in progress */}
            {data?.status === 'SUCCESS' && data.aiSolutionsStatus === 'IN_PROGRESS' && (
                <div className="mb-6 p-4 bg-muted/50 rounded-lg max-w-md">
                    <p className="text-sm text-muted-foreground">
                        🤖 AI is analyzing your performance data to generate personalized optimization suggestions...
                    </p>
                </div>
            )}
            
            <div className="w-full max-w-md bg-muted rounded-full h-2.5 mb-4">
                <div 
                    className="bg-primary h-2.5 rounded-full transition-all duration-1000" 
                    style={{ width: `${getProgressPercentage()}%` }}
                ></div>
            </div>
            <p className="text-sm text-muted-foreground">Status: {data?.status || 'Initializing...'}</p>
            
            {/* Show task creation error if failed */}
            {data?.taskCreationStatus === 'FAILED' && data.taskCreationError && (
                <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-lg max-w-md">
                    <p className="text-sm">
                        Task creation failed: {data.taskCreationError}
                    </p>
                    <p className="text-xs mt-1 opacity-80">
                        Your report is still available, but optimization tasks couldn't be created.
                    </p>
                </div>
            )}
            
            <div className="flex gap-3 mt-8">
                {/* Disable cancel button during active processing */}
                <Button 
                    variant="outline" 
                    onClick={onBack}
                    disabled={data?.status === 'RUNNING' || data?.status === 'PENDING'}
                >
                    {data?.status === 'RUNNING' || data?.status === 'PENDING' 
                        ? 'Processing...' 
                        : 'Cancel and Go Back'
                    }
                </Button>
                
                {/* Emergency exit button when approaching timeout */}
                {(() => {
                    const elapsedTime = Date.now() - startTime;
                    const currentData = data || null;
                    const dynamicTimeout = getCurrentTimeout(currentData);
                    const emergencyThreshold = dynamicTimeout * 0.9; // 90% of current timeout
                    
                    return elapsedTime > emergencyThreshold && (
                        <Button 
                            variant="secondary" 
                            onClick={() => {
                                // Try to navigate to reports page as fallback
                                navigate('/reports');
                                toast.error('Report generation timed out. Please check your reports list or try again.');
                            }}
                        >
                            Exit to Reports
                        </Button>
                    );
                })()}
            </div>
        </div>
    );
};
